'use client';

import { forwardRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { TableRootContext } from './context/table-root.context';
import { useSortableTable } from './hooks/use-sortable-table';
import { tableRootRecipe } from './styles/table-root.styles';
import type { TableRootPropsInterface } from './types/table-root.types';

/**
 * A native [`<table>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/table) element.
 *
 * @since 8.0.0
 */
export const TableRoot = forwardRef<HTMLTableElement, TableRootPropsInterface>(
  (props: TableRootPropsInterface, forwardedRef) => {
    const {
      alignments = [],
      appearance = 'grid',
      className,
      css: cssProp,
      size = 'md',
      ...rest
    } = props;

    const { context: sortableContext, rest: remainingProps } = useSortableTable(rest);

    const styles = css(tableRootRecipe.raw({ appearance, size }), cssProp);

    return (
      <TableRootContext.Provider
        value={{
          alignments,
          appearance,
          size,
          ...sortableContext,
        }}
      >
        <table
          ref={forwardedRef}
          data-c11n-component="Table.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          {...remainingProps}
        />
      </TableRootContext.Provider>
    );
  },
);

TableRoot.displayName = 'Table.Root';
